import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Banknote,
  FileBadge,
  Handshake,
  Languages,
  Loader2,
  ShieldCheck,
  Truck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  "truck-import": Truck,
  "truck-export": Truck,
  "truck": Truck,
  "file-badge": FileBadge,
  "languages": Languages,
  "shield-check": ShieldCheck,
  "banknote": Banknote,
  "handshake": Handshake,
};

export default function ServicesPage() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || []));
  }, []);

  return (
    <div className="py-6 md:py-10" data-testid="services-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {t("services.overline")}
        </p>
        <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
          {t("services.title")}
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          {t("services.subtitle")}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {services.map((s, i) => {
          const Icon = ICON_MAP[s.icon] || Truck;
          return (
            <motion.button
              key={s.slug}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              whileHover={{ y: -3 }}
              onClick={() => setActive(s)}
              data-testid={`service-${s.slug}`}
              className="text-left rounded-2xl bg-white border border-slate-100 p-5 shadow-sm hover:shadow-md hover:border-blue-200 transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg tracking-tight text-slate-950">
                {t(`services.${s.slug}.title`)}
              </h3>
              <p className="mt-1 text-sm text-slate-500 line-clamp-3">
                {t(`services.${s.slug}.subtitle`)}
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-blue-600">
                {t("services.request_quote")} <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          );
        })}
      </div>

      <ServiceModal active={active} onClose={() => setActive(null)} />
    </div>
  );
}

function ServiceModal({ active, onClose }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = (v) => {
    if (!v) {
      setDone(false);
      setPhone("");
      setMessage("");
      onClose();
    }
  };

  const submit = async () => {
    if (!active) return;
    setLoading(true);
    try {
      await api.post("/services/quote", {
        service_slug: active.slug,
        phone,
        message,
      });
      setDone(true);
      toast.success(t("services.quote_sent"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!active} onOpenChange={reset}>
      <DialogContent data-testid="service-modal" className="max-w-md rounded-2xl">
        {done ? (
          <div className="py-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-2xl">
              ✓
            </div>
            <h3 className="mt-4 font-heading font-bold text-xl tracking-tight text-slate-950">
              {t("services.quote_success_title")}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {t("services.quote_success_desc")}
            </p>
            <Button
              onClick={() => reset(false)}
              className="mt-6 rounded-full bg-blue-600 hover:bg-blue-700 w-full"
            >
              OK
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">
                {active ? t(`services.${active.slug}.title`) : ""}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {active ? t(`services.${active.slug}.description`) : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">{t("actions.phone")}</Label>
                <Input
                  data-testid="service-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+39 333 1234567"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">{t("actions.message")}</Label>
                <Textarea
                  data-testid="service-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("services.message_placeholder")}
                  className="mt-1 rounded-xl min-h-[100px] resize-none"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => reset(false)} className="rounded-full">
                {t("common.cancel")}
              </Button>
              <Button
                data-testid="service-submit"
                disabled={loading}
                onClick={submit}
                className="rounded-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("services.request_quote")
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
